import React, { useEffect, useState } from "react";
import { Loader2, Plus, Pencil, Trash2, GripVertical } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";
import { api } from "../../lib/api";
import { tierPillClass, tierLabel } from "../../lib/adminTier";

const TIERS = ["ospite", "amico", "famiglia", "padrone"];
const fmtNumber = (n) => Number(n || 0).toLocaleString();

const empty = () => ({
  name: "",
  description: "",
  points_cost: "",
  tier_required: "ospite",
  image_url: "",
  active: true,
});

const RewardModal = ({ open, onClose, initial, onSaved }) => {
  const isEdit = !!(initial && initial.id);
  const [form, setForm] = useState(empty());
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setForm({
        name: initial?.name || "",
        description: initial?.description || "",
        points_cost: initial?.points_cost ? String(initial.points_cost) : "",
        tier_required: initial?.tier_required || "ospite",
        image_url: initial?.image_url || "",
        active: initial?.active ?? true,
      });
      setPreview(initial?.image_url || null);
      setErrors({});
    }
  }, [open, initial]);

  const validate = () => {
    const e = {};
    if (!form.name.trim() || form.name.length > 80) e.name = "1-80 characters";
    if (!form.description.trim() || form.description.length > 300) e.description = "1-300 characters";
    const pts = parseInt(form.points_cost || "0", 10);
    if (!pts || pts < 1 || pts > 100000) e.points_cost = "Must be 1 to 100,000";
    if (!TIERS.includes(form.tier_required)) e.tier_required = "Pick a tier";
    if (form.image_url && !/^https?:\/\//.test(form.image_url)) e.image_url = "Must be http(s) URL";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate() || submitting) return;
    setSubmitting(true);
    try {
      const body = {
        name: form.name.trim(),
        description: form.description.trim(),
        points_cost: parseInt(form.points_cost, 10),
        tier_required: form.tier_required,
        image_url: form.image_url.trim(),
        active: !!form.active,
      };
      if (isEdit) {
        await api.patch(`/admin/rewards/${initial.id}`, body);
        toast.success(`Reward updated: ${body.name}`);
      } else {
        await api.post("/admin/rewards", body);
        toast.success(`Reward created: ${body.name}`);
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error("Couldn't save reward. Check fields and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="reward-modal"
        className="!bg-napoli-marble !border-napoli-gold/30 !rounded-none !text-napoli-espresso !max-w-lg max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-display font-bold text-[24px] text-napoli-espresso text-left">
            {isEdit ? "Edit reward" : "New reward"}
          </DialogTitle>
          <DialogDescription className="font-body text-[13px] text-napoli-espresso-soft text-left">
            Customers see these on the rewards page. Soft-deleting hides a reward but keeps past redemptions intact.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <label className="block">
            <span className="block font-body font-semibold text-[10px] tracking-[0.22em] uppercase text-napoli-espresso-soft mb-1.5">
              Name
            </span>
            <input
              data-testid="reward-name"
              type="text"
              value={form.name}
              maxLength={80}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full bg-napoli-cream-warm border border-napoli-gold/40 px-3 py-2.5 font-body text-[14px] text-napoli-espresso focus:outline-none focus:border-napoli-oxblood transition-colors"
            />
            {errors.name && <p className="mt-1 text-[11px] text-napoli-oxblood font-body">{errors.name}</p>}
          </label>

          <label className="block">
            <span className="block font-body font-semibold text-[10px] tracking-[0.22em] uppercase text-napoli-espresso-soft mb-1.5">
              Description
            </span>
            <textarea
              data-testid="reward-description"
              value={form.description}
              maxLength={300}
              rows={3}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full bg-napoli-cream-warm border border-napoli-gold/40 px-3 py-2.5 font-body text-[13px] text-napoli-espresso focus:outline-none focus:border-napoli-oxblood transition-colors"
            />
            {errors.description && <p className="mt-1 text-[11px] text-napoli-oxblood font-body">{errors.description}</p>}
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="block font-body font-semibold text-[10px] tracking-[0.22em] uppercase text-napoli-espresso-soft mb-1.5">
                Points cost
              </span>
              <input
                data-testid="reward-points"
                type="text"
                inputMode="numeric"
                value={form.points_cost}
                onChange={(e) => setForm({ ...form, points_cost: e.target.value.replace(/[^0-9]/g, "").slice(0, 6) })}
                className="w-full bg-napoli-cream-warm border border-napoli-gold/40 px-3 py-2.5 font-body text-[14px] text-napoli-espresso focus:outline-none focus:border-napoli-oxblood transition-colors"
              />
              {errors.points_cost && <p className="mt-1 text-[11px] text-napoli-oxblood font-body">{errors.points_cost}</p>}
            </label>

            <label className="block">
              <span className="block font-body font-semibold text-[10px] tracking-[0.22em] uppercase text-napoli-espresso-soft mb-1.5">
                Tier required
              </span>
              <select
                data-testid="reward-tier"
                value={form.tier_required}
                onChange={(e) => setForm({ ...form, tier_required: e.target.value })}
                className="w-full bg-napoli-cream-warm border border-napoli-gold/40 px-3 py-2.5 font-body text-[14px] text-napoli-espresso focus:outline-none focus:border-napoli-oxblood transition-colors"
              >
                {TIERS.map((t) => (
                  <option key={t} value={t}>{tierLabel(t)}</option>
                ))}
              </select>
            </label>
          </div>

          <label className="block">
            <span className="block font-body font-semibold text-[10px] tracking-[0.22em] uppercase text-napoli-espresso-soft mb-1.5">
              Image URL (optional)
            </span>
            <div className="flex gap-2">
              <input
                data-testid="reward-image-url"
                type="text"
                value={form.image_url}
                onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                placeholder="https://..."
                className="flex-1 bg-napoli-cream-warm border border-napoli-gold/40 px-3 py-2.5 font-body text-[13px] text-napoli-espresso focus:outline-none focus:border-napoli-oxblood transition-colors"
              />
              <button
                type="button"
                data-testid="reward-image-preview-btn"
                onClick={() => setPreview(form.image_url)}
                className="px-4 py-2.5 border border-napoli-gold/40 bg-napoli-cream-warm text-napoli-espresso font-body font-semibold text-[11px] tracking-[0.18em] uppercase hover:border-napoli-oxblood/40 transition-colors"
              >
                Preview
              </button>
            </div>
            {errors.image_url && <p className="mt-1 text-[11px] text-napoli-oxblood font-body">{errors.image_url}</p>}
            {preview ? (
              <div className="mt-3 w-full max-h-48 overflow-hidden border border-napoli-gold/30">
                <img src={preview} alt="preview" className="w-full max-h-48 object-cover" />
              </div>
            ) : null}
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              data-testid="reward-active"
              checked={!!form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
              className="w-4 h-4 accent-napoli-oxblood"
            />
            <span className="font-body font-semibold text-[12px] text-napoli-espresso">
              Active · visible to customers
            </span>
          </label>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              data-testid="reward-cancel"
              onClick={onClose}
              className="flex-1 border border-napoli-espresso/40 text-napoli-espresso font-body font-semibold text-[12px] tracking-[0.22em] uppercase py-3 hover:bg-napoli-cream-warm transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="reward-submit"
              disabled={submitting}
              onClick={submit}
              className="flex-1 bg-napoli-oxblood text-napoli-cream font-body font-semibold text-[12px] tracking-[0.22em] uppercase py-3 hover:bg-napoli-oxblood-dark transition-colors disabled:opacity-50"
            >
              {submitting ? "…" : isEdit ? "Save changes" : "Create reward"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const DeleteConfirm = ({ open, onClose, reward, onConfirmed }) => {
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    if (!reward || submitting) return;
    setSubmitting(true);
    try {
      await api.delete(`/admin/rewards/${reward.id}`);
      toast.success(`Hidden: ${reward.name}`);
      onConfirmed();
      onClose();
    } catch (_) {
      toast.error("Couldn't hide reward.");
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="reward-delete-modal"
        className="!bg-napoli-marble !border-napoli-gold/30 !rounded-none !text-napoli-espresso !max-w-sm"
      >
        <DialogHeader>
          <DialogTitle className="font-display font-bold text-[24px] text-napoli-espresso text-left">
            Hide reward?
          </DialogTitle>
          <DialogDescription className="font-body text-[13px] text-napoli-espresso-soft text-left">
            Customers can no longer redeem <span className="font-semibold not-italic text-napoli-espresso">{reward?.name}</span>, but past redemptions stay intact.
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            data-testid="reward-delete-cancel"
            className="flex-1 border border-napoli-espresso/40 text-napoli-espresso font-body font-semibold text-[12px] tracking-[0.22em] uppercase py-3 hover:bg-napoli-cream-warm transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={submitting}
            onClick={submit}
            data-testid="reward-delete-confirm"
            className="flex-1 bg-napoli-oxblood text-napoli-cream font-body font-semibold text-[12px] tracking-[0.22em] uppercase py-3 hover:bg-napoli-oxblood-dark transition-colors disabled:opacity-50"
          >
            {submitting ? "…" : "Hide"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const RewardRow = ({ r, onEdit, onDelete }) => (
  <tr
    data-testid={`reward-row-${r.id}`}
    className="border-b border-napoli-gold/15 last:border-0 hover:bg-napoli-cream-warm/60 transition-colors"
  >
    <td className="px-4 py-3 w-8 text-napoli-espresso-soft">
      <GripVertical size={14} className="opacity-40" />
    </td>
    <td className="px-4 py-3 w-20">
      {r.image_url ? (
        <img src={r.image_url} alt="" className="w-12 h-12 object-cover" loading="lazy" />
      ) : null}
    </td>
    <td className="px-4 py-3">
      <p className="font-body font-semibold text-[14px] text-napoli-espresso">{r.name}</p>
      <p className="font-body text-[12px] text-napoli-espresso-soft truncate max-w-md">{r.description}</p>
    </td>
    <td className="px-4 py-3">
      <span className={tierPillClass(r.tier_required)}>{tierLabel(r.tier_required)}</span>
    </td>
    <td className="px-4 py-3">
      <span className="font-body font-bold text-[16px] text-napoli-gold tabular-nums">{fmtNumber(r.points_cost)}</span>
    </td>
    <td className="px-4 py-3 font-body italic text-[11px] text-napoli-espresso-soft">
      {fmtNumber(r.redemption_count)} redeemed
    </td>
    <td className="px-4 py-3">
      <span
        className={`inline-flex items-center px-2 py-1 font-body font-semibold text-[10px] tracking-[0.22em] uppercase border ${
          r.active
            ? "bg-napoli-gold/15 text-napoli-gold border-napoli-gold/50"
            : "bg-napoli-cream-warm text-napoli-espresso-soft border-napoli-espresso-soft/40"
        }`}
      >
        {r.active ? "Active" : "Inactive"}
      </span>
    </td>
    <td className="px-4 py-3 text-right whitespace-nowrap">
      <button
        type="button"
        data-testid={`reward-edit-${r.id}`}
        onClick={() => onEdit(r)}
        className="inline-flex items-center justify-center w-8 h-8 text-napoli-gold hover:bg-napoli-gold/10 transition-colors mr-1"
        aria-label="Edit"
      >
        <Pencil size={14} />
      </button>
      <button
        type="button"
        data-testid={`reward-delete-${r.id}`}
        onClick={() => onDelete(r)}
        className="inline-flex items-center justify-center w-8 h-8 text-napoli-oxblood hover:bg-napoli-oxblood/10 transition-colors"
        aria-label="Delete"
      >
        <Trash2 size={14} />
      </button>
    </td>
  </tr>
);

const RewardCardMobile = ({ r, onEdit, onDelete }) => (
  <div
    data-testid={`reward-card-${r.id}`}
    className="relative bg-napoli-marble border border-napoli-gold/20 p-4 flex gap-3"
  >
    {r.image_url ? (
      <img src={r.image_url} alt="" className="w-16 h-16 object-cover flex-shrink-0" />
    ) : null}
    <div className="flex-1 min-w-0">
      <p className="font-body font-semibold text-[14px] text-napoli-espresso truncate">{r.name}</p>
      <p className="font-body text-[12px] text-napoli-espresso-soft line-clamp-1">{r.description}</p>
      <div className="mt-1.5 flex items-center gap-2 flex-wrap">
        <span className={tierPillClass(r.tier_required)}>{tierLabel(r.tier_required)}</span>
        <span className="font-body font-bold text-[14px] text-napoli-gold tabular-nums">{fmtNumber(r.points_cost)} pts</span>
        {!r.active ? (
          <span className="inline-flex items-center px-2 py-0.5 bg-napoli-cream-warm text-napoli-espresso-soft border border-napoli-espresso-soft/40 font-body font-semibold text-[9px] tracking-[0.22em] uppercase">
            Inactive
          </span>
        ) : null}
      </div>
    </div>
    <div className="absolute top-2 right-2 flex gap-1">
      <button
        type="button"
        data-testid={`reward-edit-mobile-${r.id}`}
        onClick={() => onEdit(r)}
        className="w-7 h-7 inline-flex items-center justify-center text-napoli-gold hover:bg-napoli-gold/10"
      >
        <Pencil size={12} />
      </button>
      <button
        type="button"
        data-testid={`reward-delete-mobile-${r.id}`}
        onClick={() => onDelete(r)}
        className="w-7 h-7 inline-flex items-center justify-center text-napoli-oxblood hover:bg-napoli-oxblood/10"
      >
        <Trash2 size={12} />
      </button>
    </div>
  </div>
);

const AdminRewardsPage = () => {
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [editing, setEditing] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [deletingReward, setDeletingReward] = useState(null);

  const reload = async () => {
    setLoading(true);
    setErr(null);
    try {
      const { data } = await api.get("/admin/rewards");
      setRewards(data.rewards || []);
    } catch (e) {
      setErr("Couldn't load rewards.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const activeCount = rewards.filter((r) => r.active).length;

  return (
    <section data-testid="admin-rewards-page" className="max-w-6xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="font-body font-semibold text-[10px] tracking-[0.3em] uppercase text-napoli-oxblood">
            Rewards catalog
          </p>
          <h1 className="mt-2 font-display font-bold text-[28px] sm:text-[32px] text-napoli-espresso leading-tight">
            What customers can earn.
          </h1>
          <p className="mt-1 font-body italic text-[14px] text-napoli-espresso-soft">
            {activeCount} active reward{activeCount === 1 ? "" : "s"} across {[...new Set(rewards.map((r) => r.tier_required))].length} tier{[...new Set(rewards.map((r) => r.tier_required))].length === 1 ? "" : "s"}.
          </p>
        </div>
        <button
          type="button"
          data-testid="reward-new"
          onClick={() => { setEditing(null); setModalOpen(true); }}
          className="inline-flex items-center gap-2 bg-napoli-oxblood text-napoli-cream px-5 py-3 font-body font-semibold text-[12px] tracking-[0.22em] uppercase hover:bg-napoli-oxblood-dark transition-colors self-start"
        >
          <Plus size={14} /> New reward
        </button>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3" data-testid="rewards-mobile">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-24 bg-napoli-marble animate-pulse" />)
        ) : err ? (
          <p className="font-body italic text-napoli-oxblood text-center py-8">{err}</p>
        ) : rewards.length === 0 ? (
          <div className="bg-napoli-marble border border-napoli-gold/20 p-6 text-center font-body italic text-[13px] text-napoli-espresso-soft">
            No rewards yet. Tap "New reward" to add one.
          </div>
        ) : (
          rewards.map((r) => (
            <RewardCardMobile
              key={r.id}
              r={r}
              onEdit={(x) => { setEditing(x); setModalOpen(true); }}
              onDelete={setDeletingReward}
            />
          ))
        )}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block bg-napoli-marble border border-napoli-gold/20">
        <table className="w-full" data-testid="rewards-table">
          <thead>
            <tr className="border-b border-napoli-gold/30">
              <th className="px-4 py-3 w-8" />
              <th className="px-4 py-3 w-20 text-left font-body font-semibold text-[10px] tracking-[0.22em] uppercase text-napoli-espresso-soft">Image</th>
              <th className="text-left px-4 py-3 font-body font-semibold text-[10px] tracking-[0.22em] uppercase text-napoli-espresso-soft">Reward</th>
              <th className="text-left px-4 py-3 font-body font-semibold text-[10px] tracking-[0.22em] uppercase text-napoli-espresso-soft">Tier</th>
              <th className="text-left px-4 py-3 font-body font-semibold text-[10px] tracking-[0.22em] uppercase text-napoli-espresso-soft">Cost</th>
              <th className="text-left px-4 py-3 font-body font-semibold text-[10px] tracking-[0.22em] uppercase text-napoli-espresso-soft">Redeemed</th>
              <th className="text-left px-4 py-3 font-body font-semibold text-[10px] tracking-[0.22em] uppercase text-napoli-espresso-soft">Status</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i} className="border-b border-napoli-gold/15">
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((j) => (
                    <td key={j} className="px-4 py-4">
                      <div className="h-4 bg-napoli-cream-warm animate-pulse" />
                    </td>
                  ))}
                </tr>
              ))
            ) : err ? (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center font-body italic text-napoli-oxblood">{err}</td>
              </tr>
            ) : rewards.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-12 text-center font-body italic text-[13px] text-napoli-espresso-soft">
                  No rewards yet. Click "+ NEW REWARD" to add one.
                </td>
              </tr>
            ) : (
              rewards.map((r) => (
                <RewardRow
                  key={r.id}
                  r={r}
                  onEdit={(x) => { setEditing(x); setModalOpen(true); }}
                  onDelete={setDeletingReward}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      <RewardModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        initial={editing}
        onSaved={reload}
      />
      <DeleteConfirm
        open={!!deletingReward}
        onClose={() => setDeletingReward(null)}
        reward={deletingReward}
        onConfirmed={reload}
      />
    </section>
  );
};

export default AdminRewardsPage;
